// frontend/src/features/customFields/CustomFieldsPanel.tsx
// ADR-095 — Custom Fields Framework
//
// یک پنل embeddable که داخل صفحهٔ Detail هر موجودیت دیگر (مثلاً
// MaterialDetailPage) قرار می‌گیرد و فیلدهای سفارشی فعالِ آن نوع موجودیت
// را نمایش/ویرایش می‌کند. مصرف نمونه در انتهای فایل (کامنت).

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import jsonLogic from "json-logic-js";
import {
  getCustomFieldsForTarget,
  setCustomFieldValues,
  uploadCustomFieldMedia,
} from "./api";
import { axiosInstance } from "@/api/client";
import type {
  CustomFieldDefinition,
  CustomFieldValue as FieldValue,
} from "./types";

interface CustomFieldsPanelProps {
  /** "app_label.model" — باید دقیقاً همان چیزی باشد که در تعریف فیلد ثبت شده. */
  targetContentType: string;
  targetObjectId?: string;
  /** کانتکست فرم (مثل نوع متریال) برای ارزیابی شروط نمایش فیلدها */
  context?: Record<string, any>;
  /** اگر false باشد، پنل فقط خواندنی نمایش داده می‌شود (بدون دکمهٔ ذخیره). */
  editable?: boolean;
  hideSaveButton?: boolean;
  onChange?: (values: Record<string, FieldValue>, isValid: boolean) => void;
}

type DraftValues = Record<string, FieldValue>;

export function CustomFieldsPanel({
  targetContentType,
  targetObjectId,
  context = {},
  editable = true,
  hideSaveButton = false,
  onChange,
}: CustomFieldsPanelProps) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [allDefinitions, setAllDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [draft, setDraft] = useState<DraftValues>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomFieldsForTarget(targetContentType, targetObjectId);
      setAllDefinitions(data.definitions);
      setDraft(data.values as DraftValues);
        // Validates all required fields are filled
        const isValid = data.definitions.every((def) => !def.required || data.values[def.field_key]);
        if (onChangeRef.current) onChangeRef.current(data.values as DraftValues, isValid);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }, [targetContentType, targetObjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const definitions = useMemo(() => {
    // ترکیب کانتکست خارجی (مثل material_type) و کانتکست داخلی فرم (مثل مقادیر تیک خورده)
    const evaluationContext = { ...context, ...draft };
    return allDefinitions.filter((def) => {
      if (!def.conditions || Object.keys(def.conditions).length === 0) {
        return true; // No conditions = always visible
      }
      try {
        return jsonLogic.apply(def.conditions, evaluationContext);
      } catch (err) {
        console.error(`Error evaluating condition for field ${def.field_key}:`, err);
        return false;
      }
    });
  }, [allDefinitions, context, draft]);



  // فقط مقادیری را نگه دار که فیلد آن‌ها قابل رؤیت است
  const getVisibleDraft = useCallback((currentDraft: DraftValues, currentDefs: CustomFieldDefinition[]) => {
    return Object.fromEntries(
      Object.entries(currentDraft).filter(([k]) => currentDefs.some((d) => d.field_key === k))
    );
  }, []);

  if (loading) {
    return <div className="text-sm text-gray-500">در حال بارگذاری فیلدهای سفارشی…</div>;
  }

  if (error) {
    return <div className="text-sm text-red-600">خطا: {error}</div>;
  }

  if (definitions.length === 0 && allDefinitions.length === 0) {
    return null; // هیچ فیلد سفارشی‌ای برای این نوع موجودیت تعریف نشده
  }

  if (definitions.length === 0) {
    return null; // فیلدی برای این کانتکست وجود ندارد
  }

  const updateDraft = (key: string, value: FieldValue) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (onChangeRef.current) {
        const isValid = definitions.every((def) => !def.required || next[def.field_key]);
        onChangeRef.current(next, isValid);
      }
      return next;
    });
    setSavedAt(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!targetObjectId) {
      setError("شناسه موجودیت یافت نشد (نمی‌توان ذخیره کرد).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const visibleDraft = getVisibleDraft(draft, definitions);
      await setCustomFieldValues(targetContentType, targetObjectId, visibleDraft);
      setSavedAt(Date.now());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{
      padding: "1.25rem",
      background: "rgba(255,255,255,0.03)",
      border: "1px solid var(--glass-border)",
      borderRadius: "10px",
      display: "flex",
      flexDirection: "column",
      gap: "1.5rem"
    }} dir="rtl">
      <h3 style={{
        fontSize: "0.85rem",
        fontWeight: "bold",
        color: "var(--color-text-muted)",
        marginBottom: "0.5rem",
      }}>فیلدهای سفارشی</h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {definitions.map((def) => (
          <div key={def.id} style={{ width: '100%' }}>
            <CustomFieldInput
              definition={def}
              value={draft[def.field_key] ?? null}
              disabled={!editable}
              onChange={(value) => updateDraft(def.field_key, value)}
            />
          </div>
        ))}
      </div>

      {editable && !hideSaveButton && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "در حال ذخیره…" : "ذخیرهٔ فیلدهای سفارشی"}
          </button>
          {savedAt && !saving && (
            <span className="text-xs text-emerald-700">ذخیره شد.</span>
          )}
        </div>
      )}
    </section>
  );
}

interface CustomFieldInputProps {
  definition: CustomFieldDefinition;
  value: FieldValue;
  disabled: boolean;
  onChange: (value: FieldValue) => void;
}

function CustomFieldInput({ definition, value, disabled, onChange }: CustomFieldInputProps) {
  const labelNode = (
    <label className="input-label" style={{ display: "block", marginBottom: "0.5rem" }}>
      {definition.label}
      {definition.required && <span style={{ color: "red" }}> *</span>}
    </label>
  );

  switch (definition.field_type) {
    case "textarea":
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <textarea
            className="input-field"
            style={{ width: "100%", resize: "vertical" }}
            value={(value as string) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
          />
          {definition.help_text && (
            <p style={{ marginTop: "4px", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{definition.help_text}</p>
          )}
        </div>
      );

    case "number":
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <input
            type="number"
            className="input-field"
            style={{ width: "100%" }}
            value={value === null || value === undefined ? "" : (value as number)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          />
        </div>
      );

    case "checkbox":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.6rem 1rem", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid var(--glass-border)", width: "100%" }}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: "1rem", height: "1rem", cursor: "pointer" }}
          />
          <span style={{ fontSize: "0.9rem", color: "var(--color-text-main)", cursor: "pointer" }}>
            {definition.label}
            {definition.required && <span style={{ color: "red" }}> *</span>}
          </span>
        </div>
      );

    case "date":
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <input
            type="date"
            className="input-field"
            style={{ width: "100%" }}
            value={(value as string) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          />
        </div>
      );

    case "select":
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <select
            className="input-field"
            style={{ width: "100%" }}
            value={(value as string) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="">— انتخاب کنید —</option>
            {definition.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );

    case "file":
    case "image":
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <CustomFieldMediaUploader
            value={value as string}
            type={definition.field_type}
            disabled={disabled}
            onChange={onChange}
          />
          {definition.help_text && (
            <p style={{ marginTop: "4px", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{definition.help_text}</p>
          )}
        </div>
      );

    case "text":
    default:
      return (
        <div style={{ width: "100%" }}>
          {labelNode}
          <input
            type="text"
            className="input-field"
            style={{ width: "100%" }}
            value={(value as string) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          {definition.help_text && (
            <p style={{ marginTop: "4px", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{definition.help_text}</p>
          )}
        </div>
      );
  }
}

function CustomFieldMediaUploader({ value, type, disabled, onChange }: { value: string | null; type: "file" | "image"; disabled: boolean; onChange: (v: string | null) => void; }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (value && type === "image") {
      axiosInstance.get(`/api/v1/custom-field-media/${value}/download/`, { responseType: "blob" })
        .then(res => {
          if (active) {
            setPreviewUrl(URL.createObjectURL(res.data));
          }
        })
        .catch(console.error);
    }
    return () => { active = false; };
  }, [value, type]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const res = await uploadCustomFieldMedia(file);
      if (res?.id) {
        onChange(res.id);
      }
    } catch (err) {
      alert("خطا در بارگذاری فایل: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!value) return;
    try {
      const res = await axiosInstance.get(`/api/v1/custom-field-media/${value}/download/`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "download"; // Server usually sets Content-Disposition header with real filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("خطا در دانلود فایل");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {value ? (
        <div className="flex items-center gap-4">
          {type === "image" && previewUrl ? (
            <img src={previewUrl} alt="preview" className="h-16 w-16 object-cover rounded border" />
          ) : (
            <span className="text-sm text-emerald-600 bg-emerald-50 px-2 py-1 rounded">فایل بارگذاری شده</span>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownloadClick}
              className="text-xs text-blue-600 underline"
            >
              دانلود
            </button>
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-xs text-red-600 underline"
              >
                حذف
              </button>
            )}
          </div>
        </div>
      ) : (
        <input
          type="file"
          accept={type === "image" ? "image/*" : undefined}
          disabled={disabled || uploading}
          onChange={handleFileChange}
          className="text-sm"
        />
      )}
      {uploading && <span className="text-xs text-gray-500">در حال آپلود...</span>}
    </div>
  );
}

/*
مصرف نمونه، مثلاً داخل MaterialDetailPage.tsx:

<CustomFieldsPanel
  targetContentType="inventory.materialfamily"
  targetObjectId={material.id}
/>
*/
